import Vue from "vue";
import { graphGet } from "@/helpers/api/apiGraph";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";
import DriveItem from "@/models/Documents/DriveItem";
import { useFeaturesStore } from "@/stores/features";

export const driveItems_state = {
  driveMap: {}, // driveId -> itemId -> DriveItem
  pathToDriveItemMap: {} // driveId -> path -> { driveId, itemId }
};

export const driveItems_actions = {
  /**
   * Abstracted function to reload drive folder no matter if it is a regular folder or a combidrive
   * @param { string } driveId DriveId to reload
   * @param { string } itemId ItemId to reload
   * @returns { Promise } Promise that the function will end
   */
  async reload(driveId, itemId) {
    const combiDriveDef = this.controlledAreaKeysFromDriveIdAndItemId(driveId, itemId);

    if (combiDriveDef) {
      this.reloadCombiDriveFolder(combiDriveDef.system, combiDriveDef.entity, combiDriveDef.group, itemId);
    }

    return this.reloadDriveItem(driveId, itemId);
  },

  /**
   * Load driveItem - if it is in state then do nothing, if not load it from graph
   * @param { string } driveId id of drive to load
   * @param { string } itemId id of driveItem to load
   * @param { object } additionalProperties additional properties to add to driveItems and children
   * @returns { object } DriveItem
   */
  async loadDriveItem(driveId, itemId, additionalProperties) {
    if (!this.driveMap[driveId] || !this.driveMap[driveId][DriveItemHelper.getItemId(itemId)]) {
      await this.reloadDriveItem(driveId, itemId, additionalProperties);
    }
    return this.driveMap[driveId][DriveItemHelper.getItemId(itemId)];
  },

  /**
   * Reload driveItem and load permissions for driveItems children
   * @param { string } driveId id of drive to load
   * @param { string } itemId id of driveItem to load
   * @param { object } additionalProperties additional properties to add to driveItems and children
   * @returns {object} Driveitem
   */
  async reloadDriveItem(driveId, itemId, additionalProperties) {
    const driveItem = await this.fetchDriveItem(driveId, itemId);

    if (driveItem && !driveItem.error) {
      if (additionalProperties) {
        Object.assign(driveItem, additionalProperties);
        driveItem.children.forEach(child => {
          Object.assign(child, additionalProperties);
        });
      }

      if (!driveItem.nctType) {
        const rootOfDriveTheDriveItemIsLocatedOn = await this.calculateDriveItemsRoot(driveId, itemId);

        driveItem.nctSystem = rootOfDriveTheDriveItemIsLocatedOn.nctSystem;
        driveItem.nctGroup = rootOfDriveTheDriveItemIsLocatedOn.nctGroup;
        driveItem.nctType = rootOfDriveTheDriveItemIsLocatedOn.nctType;
        driveItem.nctIdentifier = rootOfDriveTheDriveItemIsLocatedOn.nctIdentifier;
        driveItem.nctBehavior = rootOfDriveTheDriveItemIsLocatedOn.nctBehavior;

        driveItem.childrenList?.forEach(c => {
          c.nctSystem = rootOfDriveTheDriveItemIsLocatedOn.nctSystem;
          c.nctType = rootOfDriveTheDriveItemIsLocatedOn.nctType;
          c.nctGroup = rootOfDriveTheDriveItemIsLocatedOn.nctGroup;
          c.nctIdentifier = rootOfDriveTheDriveItemIsLocatedOn.nctIdentifier;
          c.nctBehaviour = rootOfDriveTheDriveItemIsLocatedOn.nctBehaviour;
        });
      }

      /*
      // Filter out hidden driveItems
      if (driveItem.children) {
        const listOfHiddenItems = this.hiddenSharedFiles;

        driveItem.children = driveItem.children.filter(
          c => !listOfHiddenItems.some(h => c.id === h.itemId && DriveItemHelper.getDriveId(c) === h.driveId)
        );
      }
*/
      /*
      // Load permissions for children
      if (driveItem.children) {
        await dispatch("reloadPermissionsForMultipleDriveItems", { driveItem: driveItem.children });
      }
*/
      /*
      if (
        getters["IS_DRIVEITEM_ROOT_FOR_SECTION"](driveId, itemId) ||
        getters["IS_DRIVEITEM_VIRTUAL_ROOT_FOR_DRIVE"](driveId, itemId)
      ) {
        driveItem.name = "root";
        if (driveItem.remoteItem) {
          delete driveItem.remoteItem.parentReference.id;
        } else {
          delete driveItem.parentReference.id;
        }
      }
*/
    }
    if (!this.driveMap[driveId]) {
      Vue.set(this.driveMap, driveId, {});
    }
    Vue.set(this.driveMap[driveId], itemId, driveItem);

    return this.driveMap[driveItem.parentReference.driveId][driveItem.id];
  },

  async loadDriveItemWithoutDecorationgWithNCTProps(driveId, itemId) {
    if (!this.driveMap[driveId] || !this.driveMap[driveId][DriveItemHelper.getItemId(itemId)]) {
      await this.reloadDriveItemWithoutDecorationgWithNCTProps(driveId, itemId);
    }
    return this.driveMap[driveId][DriveItemHelper.getItemId(itemId)];
  },

  async reloadDriveItemWithoutDecorationgWithNCTProps(driveId, itemId) {
    const driveItem = await this.fetchDriveItem(driveId, itemId);

    if (!this.driveMap[driveId]) {
      Vue.set(this.driveMap, driveId, {});
    }
    Vue.set(this.driveMap[driveId], itemId, driveItem);

    return driveItem;
  },

  /**
   * Fetch the driveItem for driveId and itemId - this should only be used inside store as driveItem is not cached in store.
   * @param { string } driveId DriveId
   * @param { string } itemId ItemId
   * @returns { Promise<object>} Promise with driveItem
   */
  async fetchDriveItem(driveId, itemId) {
    const featureStore = useFeaturesStore();
    let driveItem;
    try {
      if (featureStore.isFeatureEnabled("DocumentSensitivityLabels")) {
        // If sensitivity labels is enabled first fetch the driveItem without children
        const driveItemLoaded = await graphGet(
          `${DriveItemHelper.getDriveUri(driveId)}/items/${DriveItemHelper.getItemId(itemId)}`
        );

        driveItem = new DriveItem(driveItemLoaded);

        // and then fetch the children with the listitem fields
        const driveItemChildren = await graphGet(
          `${DriveItemHelper.getDriveUri(driveId)}/items/${DriveItemHelper.getItemId(
            itemId
          )}/children?$expand=listItem($expand=fields($select=_DisplayName))`
        );
        driveItem.children = driveItemChildren.value;
      } else {
        // If sensitivity labels is not enabled fetch the driveItem with children
        const driveItemLoaded = await graphGet(
          `${DriveItemHelper.getDriveUri(driveId)}/items/${DriveItemHelper.getItemId(itemId)}/?$expand=children`
        );
        driveItem = new DriveItem(driveItemLoaded);
      }

      // If we already have driveItem in store use the metaprops from it and update the current item
      if (this.driveMap[driveId] && this.driveMap[driveId][itemId] && !this.driveMap[driveId][itemId].error) {
        const driveItemToReplaceInStore = this.driveMap[driveId][itemId];

        driveItem.nctSystem = driveItemToReplaceInStore.nctSystem;
        driveItem.nctType = driveItemToReplaceInStore.nctType;
        driveItem.nctGroup = driveItemToReplaceInStore.nctGroup;
        driveItem.nctIdentifier = driveItemToReplaceInStore.nctIdentifier;
        driveItem.nctBehaviour = driveItemToReplaceInStore.nctBehaviour;

        driveItem.childrenList?.forEach(c => {
          c.nctSystem = driveItemToReplaceInStore.nctSystem;
          c.nctType = driveItemToReplaceInStore.nctType;
          c.nctGroup = driveItemToReplaceInStore.nctGroup;
          c.nctIdentifier = driveItemToReplaceInStore.nctIdentifier;
          c.nctBehaviour = driveItemToReplaceInStore.nctBehaviour;
        });
      }

      return driveItem;
    } catch (e) {
      if (e.response && (e.response.status === 401 || e.response.status === 404 || e.response.status === 403)) {
        return {
          id: itemId,
          parentReference: {
            driveId: driveId
          },
          error: {
            code: "DriveItem.Http404ItemNotFound"
          }
        };
      }
      return null;
    }
  },

  setNCTMetaOnStoreDriveItems(driveId, itemId, metaproperties) {
    let driveItem = this.driveItem(driveId, itemId);

    if (driveItem) {
      driveItem = Object.assign(driveItem, metaproperties);

      driveItem.childrenList?.forEach(c => (c = Object.assign(c, metaproperties)));

      Vue.set(this.driveMap[driveId], itemId, driveItem);
    }
  },

  async loadDriveItemFromPath(driveId, path, additionalProperties) {
    if (!this.pathToDriveItemMap[driveId] || !this.pathToDriveItemMap[driveId][path]) {
      await this.reloadDriveItemFromPath(driveId, path, additionalProperties);
    }
    const itemId = this.pathToDriveItemMap[driveId][path];
    if (itemId) {
      return await this.loadDriveItem(driveId, itemId, additionalProperties);
    }
    return null;
  },

  async reloadDriveItemFromPath(driveId, path, additionalProperties = {}) {
    try {
      const featureStore = useFeaturesStore();
      let driveItem;

      if (featureStore.isFeatureEnabled("DocumentSensitivityLabels")) {
        // If sensitivity labels is enabled first fetch the driveItem without children
        const driveItemLoaded = await graphGet(`${DriveItemHelper.getDriveUri(driveId)}/root:${path}`);

        driveItem = new DriveItem(driveItemLoaded);

        // and then fetch the children with the listitem fields
        const driveItemChildren = await graphGet(
          `${DriveItemHelper.getDriveUri(driveId)}/items/${DriveItemHelper.getItemId(
            driveItem.id
          )}/children?$expand=listItem($expand=fields($select=_DisplayName))`
        );
        driveItem.children = driveItemChildren.value;
      } else {
        // If sensitivity labels is not enabled fetch the driveItem with children
        const driveItemLoaded = await graphGet(
          `${DriveItemHelper.getDriveUri(driveId)}/root:/${path}/?$expand=children`
        );
        driveItem = new DriveItem(driveItemLoaded);
      }

      if (additionalProperties) {
        Object.assign(driveItem, additionalProperties);
        driveItem.children.forEach(child => {
          Object.assign(child, additionalProperties);
        });
      }

      if (!driveItem.nctType) {
        const rootOfDriveTheDriveItemIsLocatedOn = await this.calculateDriveItemsRoot(driveId, driveItem.id);

        driveItem.nctSystem = rootOfDriveTheDriveItemIsLocatedOn.nctSystem;
        driveItem.nctGroup = rootOfDriveTheDriveItemIsLocatedOn.nctGroup;
        driveItem.nctType = rootOfDriveTheDriveItemIsLocatedOn.nctType;
        driveItem.nctIdentifier = rootOfDriveTheDriveItemIsLocatedOn.nctIdentifier;
        driveItem.nctBehavior = rootOfDriveTheDriveItemIsLocatedOn.nctBehavior;

        driveItem.childrenList.forEach(c => {
          c.nctSystem = rootOfDriveTheDriveItemIsLocatedOn.nctSystem;
          c.nctType = rootOfDriveTheDriveItemIsLocatedOn.nctType;
          c.nctGroup = rootOfDriveTheDriveItemIsLocatedOn.nctGroup;
          c.nctIdentifier = rootOfDriveTheDriveItemIsLocatedOn.nctIdentifier;
          c.nctBehaviour = rootOfDriveTheDriveItemIsLocatedOn.nctBehaviour;
        });
      }

      if (!this.driveMap[driveId]) {
        Vue.set(this.driveMap, driveId, {});
      }

      if (!this.pathToDriveItemMap[driveId]) {
        this.pathToDriveItemMap[driveId] = {};
      }

      Vue.set(this.driveMap[driveId], driveItem.id, driveItem);
      Vue.set(this.pathToDriveItemMap[driveId], path, driveItem.id);

      return driveItem;
    } catch {
      if (!this.pathToDriveItemMap[driveId]) {
        this.pathToDriveItemMap[driveId] = {};
      }
      Vue.set(this.pathToDriveItemMap[driveId], path, "");
      return null;
    }
  },

  /**
   * Open driveItem in new tab
   * @param { string } driveId DriveId of the driveItem to open
   * @param { string } itemId ItemId of the driveItem to open
   */
  async openDriveItem(driveId, itemId) {
    const driveItem = await this.loadDriveItem(driveId, itemId);

    if (driveItem && driveItem.webUrl) {
      window.open(driveItem.webUrl, "_blank");
    }
  }
};

export const driveItems_getters = {
  driveItem: state => (driveId, itemId) => {
    if (state.driveMap[driveId] && state.driveMap[driveId][itemId]) {
      return state.driveMap[driveId][itemId];
    }
    return null;
  },

  driveItemWithNctMeta: state => (driveId, itemId) => {
    const driveItem = state.driveItem(driveId, itemId);

    if (!driveItem) {
      return null;
    }

    if (driveItem.error) {
      return driveItem;
    }
    driveItem.nctSpecialItem = state.specialItemObjectForDriveItem(driveId, itemId);

    driveItem.childrenList.forEach(
      c => (c.nctSpecialItem = state.specialItemObjectForDriveItem(c.parentReference.driveId, c.id))
    );

    return driveItem;
  },

  driveItemChildren: state => (driveId, itemId) => {
    if (state.driveMap[driveId] && state.driveMap[driveId][itemId]) {
      return state.driveMap[driveId][itemId].childrenList;
    }
    return [];
  },

  parentDriveItem: state => (driveId, itemId) => {
    for (let ii in state.driveMap[driveId]) {
      if (
        !state.driveMap[driveId][ii].error &&
        state.driveMap[driveId][ii].childrenList.some(child => child.id === itemId)
      ) {
        return state.driveMap[driveId][ii];
      }
    }
    return null;
  },

  calculateRootOrItemId(state) {
    return (driveId, itemId) => {
      if (itemId) {
        return itemId;
      }

      // Todo: Waiting for refactoring of sectionRootDriveSettingMap
      //for (let section in state.sectionRootDriveSettingMap) {
      //  if (state.sectionRootDriveSettingMap[section].driveId === driveId) {
      //    return state.sectionRootDriveSettingMap[section].itemId;
      //  }
      // }

      const sharedDrivesForDrive = this.sharedDrives.filter(spf => spf.driveId === driveId);
      if (sharedDrivesForDrive.length !== 0) {
        return sharedDrivesForDrive[0].itemId;
      }

      return state.driveRootItemIdMap && state.driveRootItemIdMap[driveId] ? state.driveRootItemIdMap[driveId] : "root";
    };
  },

  searchStateForDriveItem: state => (driveId, itemId) => {
    if (state.driveMap[driveId]) {
      if (state.driveMap[driveId][itemId]) {
        return state.driveMap[driveId][itemId];
      }

      Object.keys(state.driveMap[driveId]).forEach(key => {
        const foundItem = state.driveMap[driveId][key].childrenList?.find(c => c.id === itemId);
        if (foundItem) {
          return foundItem;
        }
      });
    }
    return null;
  }
};
